import { TABLET_SCREEN_SIZE } from "@brandclub/common-ui";
import {
  ButtonBase,
  CircularProgress,
  IconButton,
  InputBase,
  styled,
} from "@mui/material";
import { useState } from "react";

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
  marginBottom: "16px",
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    marginBottom: "12px",
  },
}));

const InputWrapper = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "56px",
  boxSizing: "border-box",
  border: `1.5px solid #e8e8ed`,
  borderRadius: 8,
  backgroundColor: "#fff",
  overflow: "hidden",
  transition: "border-color 0.2s ease",
  "&:focus-within": {
    borderColor: theme.vars.palette.primary.main,
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  height: "100%",
  padding: "0 16px",
  fontSize: "16px",
  fontWeight: 500,
  color: theme.vars.palette.text.primary,
  "& input": {
    padding: 0,
    "&::placeholder": {
      color: theme.vars.palette.text.secondary,
      opacity: 0.6,
    },
  },
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
  },
}));

const ApplyButton = styled(ButtonBase)(({ theme }) => ({
  height: "100%",
  padding: "0 24px",
  fontSize: "16px",
  fontWeight: 600,
  color: theme.vars.palette.primary.main,
  backgroundColor: "transparent",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 0.7,
  },
  "&:disabled": {
    opacity: 0.4,
  },
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    padding: "0 20px",
    fontSize: "14px",
  },
}));

// Applied state styles
const AppliedWrapper = styled("div")({
  display: "flex",
  alignItems: "center",
  width: "100%",
  height: "56px",
  boxSizing: "border-box",
  padding: "0 16px",
  backgroundColor: "#e8f5e9",
  border: "1.5px solid #4caf50",
  borderRadius: 8,
  gap: "12px",
});

const CheckmarkCircle = styled("div")({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  backgroundColor: "#4caf50",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const AppliedContent = styled("div")({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "2px",
});

const AppliedCodeName = styled("div")(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: "1.2",
  color: theme.vars.palette.text.primary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: "14px",
  },
}));

const AppliedCodeDescription = styled("div")(({ theme }) => ({
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "1.2",
  color: theme.vars.palette.text.secondary,
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    fontSize: "12px",
  },
}));

const RemoveButton = styled(IconButton)(({ theme }) => ({
  padding: "8px",
  color: theme.vars.palette.text.secondary,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  "& svg": {
    width: "20px",
    height: "20px",
  },
}));

const ErrorText = styled("div")(({ theme }) => ({
  fontSize: "12px",
  color: theme.vars.palette.error.main,
  paddingLeft: "12px",
}));

export interface AppliedPromoCode {
  code: string;
  description: string;
  discountAmount: number;
}

interface PromotionCodeProps {
  orderId: string;
  onApplyCode?: (code: string, orderId: string) => Promise<boolean>;
  onRemoveCode?: (code: string, orderId: string) => void;
  appliedCode?: AppliedPromoCode | null;
  disabled?: boolean;
  externalError?: string | null; // Error from parent (e.g., validation at checkout)
  onClearExternalError?: () => void; // Clear external error when user interacts
}

export const PromotionCode = ({
  orderId,
  onApplyCode,
  onRemoveCode,
  appliedCode = null,
  disabled = false,
  externalError = null,
  onClearExternalError,
}: PromotionCodeProps) => {
  const [code, setCode] = useState("");
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Display external error or internal error
  const displayError = externalError || error;

  const handleApply = async () => {
    if (!code.trim() || !orderId) return;

    setError(null);
    setIsApplying(true);

    try {
      if (onApplyCode) {
        const success = await onApplyCode(code.trim().toUpperCase(), orderId);
        if (success) {
          setCode("");
        } else {
          setError("Invalid or expired promotion code");
        }
      } else {
        // Default behavior when no handler provided
        setCode("");
      }
    } catch (err) {
      setError("Failed to apply promotion code");
    } finally {
      setIsApplying(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !disabled && code.trim()) {
      e.preventDefault();
      handleApply();
    }
  };

  const handleRemove = () => {
    if (appliedCode && orderId) {
      onRemoveCode?.(appliedCode.code, orderId);
    }
  };

  // Applied state - show success view
  if (appliedCode) {
    return (
      <Container>
        <AppliedWrapper>
          <CheckmarkCircle>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </CheckmarkCircle>
          <AppliedContent>
            <AppliedCodeName>{appliedCode.code}</AppliedCodeName>
            <AppliedCodeDescription>
              {appliedCode.description}
            </AppliedCodeDescription>
          </AppliedContent>
          <RemoveButton
            size="small"
            onClick={handleRemove}
            disabled={disabled}
            aria-label="Remove promotion code"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </RemoveButton>
        </AppliedWrapper>
      </Container>
    );
  }

  // Normal / Applying state - show input
  return (
    <Container>
      <InputWrapper>
        <StyledInputBase
          placeholder="Enter promotion code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
            onClearExternalError?.(); // Clear external error when user types
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled || isApplying}
        />
        <ApplyButton
          onClick={handleApply}
          disabled={disabled || isApplying || !code.trim()}
        >
          {isApplying ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Apply"
          )}
        </ApplyButton>
      </InputWrapper>

      {displayError && <ErrorText>{displayError}</ErrorText>}
    </Container>
  );
};
